import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import AvatarIcon from '@atlaskit/icon/core/person-avatar';
import type { ButtonItemConfig, LinkItemConfig } from '@atlassian/navigation-common/types';

import type { ForYouConfig } from '../../../common/forYouTypes';
import type { NavItemConfigInternal } from '../../../common/types';
import { FOR_YOU_TEST_ID } from './constants';
import { messages } from './messages';

const elemBefore = <AvatarIcon label="" spacing="spacious" />;
const NO_FOR_YOU_ITEM: [string, NavItemConfigInternal][] = [];

export function useForYouNavItem(forYou: ForYouConfig): [string, NavItemConfigInternal][] {
	const { formatMessage } = useIntl(); // formatMessage() is not a stable function
	// Some type magic so we can get the dependencies right for the memo and keep TypeScript happy.
	const forYouItem = (forYou as Extract<ForYouConfig, ForYouConfig>)?.forYouItem;
	const label = formatMessage(messages.label);

	return useMemo(() => {
		if (!forYouItem) {
			return NO_FOR_YOU_ITEM;
		} else {
			// Business rule: The forYouItem is not reorderable or hideable.
			const common = {
				elemBefore,
				isHideable: false,
				isReorderable: false,
				isSelected: forYouItem.isSelected,
				label,
				testId: FOR_YOU_TEST_ID,
				ItemWrapper: forYouItem?.ItemWrapper,
			};

			const value: LinkItemConfig | ButtonItemConfig = forYouItem.url
				? {
						type: 'link',
						href: forYouItem.url,
						onClick: forYouItem.onClick,
						...common,
					}
				: {
						type: 'button',
						onClick: forYouItem.onClick,
						...common,
					};

			return [[forYouItem.menuItemId ?? 'global.sidebar.forYou', value]];
		}
	}, [forYouItem, label]);
}
